/**
 * Diagram colours.
 *
 * Colours are named as CSS custom properties (declared in `src/styles/theme.css`)
 * rather than literal hex values, so a node re-themes for free when the user
 * switches to dark mode. The exporter resolves these to concrete values by
 * reading the computed style once, which is what keeps an exported SVG matching
 * what was on screen.
 *
 * A user's `@color("#…")` annotation overrides the fill and stroke; the label
 * colour is then chosen by contrast rather than trusted from the annotation, so
 * text can never end up unreadable on its own background.
 */
import type { NodeRole } from '../graph/model/types';

export interface NodeColors {
  readonly fill: string;
  readonly stroke: string;
  readonly text: string;
}

/** `var(--fs-…)` references, resolved by the browser or by the exporter. */
function tokens(name: string): NodeColors {
  return {
    fill: `var(--fs-node-${name}-fill)`,
    stroke: `var(--fs-node-${name}-stroke)`,
    text: `var(--fs-node-${name}-text)`,
  };
}

const ROLE_TOKENS: Record<NodeRole, string> = {
  start: 'start',
  end: 'end',
  'function-entry': 'start',
  'function-exit': 'end',
  action: 'action',
  decision: 'decision',
  loop: 'loop',
  call: 'call',
  io: 'io',
  note: 'note',
  error: 'error',
  return: 'return',
  junction: 'action',
};

/** The theme colours for a role, before any per-node override. */
export function colorsForRole(role: NodeRole): NodeColors {
  return tokens(ROLE_TOKENS[role]);
}

/**
 * Applies a validated `@color(...)` override.
 *
 * The custom colour becomes the fill; the stroke is a darker relative of it and
 * the text colour is picked for contrast. Users choose a colour, not a palette.
 */
export function applyColorOverride(base: NodeColors, override: string | undefined): NodeColors {
  if (!override) return base;
  const rgb = parseHex(override);
  if (!rgb) return base;
  return {
    fill: override,
    stroke: toHex(shade(rgb, -0.28)),
    text: relativeLuminance(rgb) > 0.55 ? '#14181f' : '#ffffff',
  };
}

/** Colour token for an edge, by whether it closes a loop. */
export function edgeColor(isBackEdge: boolean): string {
  return isBackEdge ? 'var(--fs-edge-back)' : 'var(--fs-edge)';
}

/* ------------------------------------------------------------------ *
 * Colour maths
 * ------------------------------------------------------------------ */

interface Rgb {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

/** Accepts `#rgb`, `#rgba`, `#rrggbb`, `#rrggbbaa`. Alpha is ignored. */
export function parseHex(value: string): Rgb | null {
  const hex = value.trim().replace(/^#/, '');
  const expand = (c: string): number => parseInt(c + c, 16);

  if (hex.length === 3 || hex.length === 4) {
    const [r, g, b] = [hex[0]!, hex[1]!, hex[2]!];
    return { r: expand(r), g: expand(g), b: expand(b) };
  }
  if (hex.length === 6 || hex.length === 8) {
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }
  return null;
}

function clamp255(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function shade(rgb: Rgb, amount: number): Rgb {
  const target = amount < 0 ? 0 : 255;
  const t = Math.abs(amount);
  return {
    r: clamp255(rgb.r + (target - rgb.r) * t),
    g: clamp255(rgb.g + (target - rgb.g) * t),
    b: clamp255(rgb.b + (target - rgb.b) * t),
  };
}

function toHex(rgb: Rgb): string {
  const part = (v: number): string => clamp255(v).toString(16).padStart(2, '0');
  return `#${part(rgb.r)}${part(rgb.g)}${part(rgb.b)}`;
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function relativeLuminance(rgb: Rgb): number {
  const channel = (value: number): number => {
    const v = value / 255;
    return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}
